import React, { useState, useEffect, useRef } from 'react';
import dictionaryData from '../vaideeka_dictionary.json';

const TAMIL_SUFFIXES = [
    'இல்', 'த்தில்', 'த்தை', 'ஐ', 'க்கு', 'ற்கு', 'ஆல்', 'ால்', 'உடன்', 'டன்',
    'கள்', 'ங்களின்', 'இன்', 'உடைய', 'மேல்', 'வரை', 'அது', 'ஆக'
];

const VaideekaDictionaryPopup = () => {
    const [popupData, setPopupData] = useState(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const popupRef = useRef(null);

    // Normalize Tamil & Sanskrit text to find dictionary match
    const findMeaning = (selectedText) => {
        if (!selectedText) return null;
        
        let cleaned = selectedText.trim().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"'“”—]/g, '');
        if (!cleaned || cleaned.length < 2) return null;

        const lowerCleaned = cleaned.toLowerCase();

        // 1. Direct exact match
        let found = dictionaryData.find(item => 
            (item.word && item.word.toLowerCase() === lowerCleaned) ||
            (item.sanskrit && item.sanskrit.toLowerCase() === lowerCleaned)
        );
        if (found) return found;

        // 2. Suffix stripping (Morphological stemming)
        for (const suffix of TAMIL_SUFFIXES) {
            if (cleaned.endsWith(suffix) && cleaned.length - suffix.length >= 2) {
                const stem = cleaned.slice(0, -suffix.length);
                found = dictionaryData.find(item => 
                    item.word.startsWith(stem) || 
                    (item.sanskrit && item.sanskrit.startsWith(stem))
                );
                if (found) return found;
            }
        }

        // 3. Substring match fallback
        found = dictionaryData.find(item => 
            (item.word && (item.word.includes(cleaned) || cleaned.includes(item.word))) ||
            (item.meaning && item.meaning.toLowerCase().includes(lowerCleaned))
        );

        return found;
    };

    useEffect(() => {
        const handleSelection = (e) => {
            // Ignore clicks inside the popup itself
            if (popupRef.current && popupRef.current.contains(e.target)) {
                return;
            }

            const selection = window.getSelection();
            const text = selection ? selection.toString().trim() : '';

            if (text && text.length >= 2) {
                const match = findMeaning(text);
                if (match) {
                    let range;
                    try {
                        range = selection.getRangeAt(0);
                    } catch (err) {
                        return;
                    }

                    const rect = range.getBoundingClientRect();
                    const top = Math.max(10, rect.bottom + window.scrollY + 10);
                    const left = Math.min(window.innerWidth - 320, Math.max(10, rect.left + window.scrollX - 40));

                    setPosition({ top, left });
                    setPopupData({
                        query: text,
                        word: match.word,
                        meaning: match.meaning,
                        category: match.category || 'Vaideeka Terms'
                    });
                    return;
                }
            }

            // Close popup if click outside
            setPopupData(null);
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setPopupData(null);
        };

        document.addEventListener('mouseup', handleSelection);
        document.addEventListener('keyup', handleKeyDown);

        return () => {
            document.removeEventListener('mouseup', handleSelection);
            document.removeEventListener('keyup', handleKeyDown);
        };
    }, []);

    if (!popupData) return null;

    return (
        <div
            ref={popupRef}
            className="fixed z-50 bg-white border border-orange-200 rounded-xl shadow-2xl p-4 max-w-sm w-80 text-gray-800 animate-in fade-in zoom-in-95 duration-200"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
            }}
        >
            <div className="flex items-start justify-between border-b border-orange-100 pb-2 mb-2">
                <div>
                    <span className="inline-block bg-orange-100 text-orange-800 text-[10px] font-bold px-2 py-0.5 rounded tracking-wide uppercase">
                        📖 வைதீக அகராதி
                    </span>
                    <h4 className="text-lg font-bold text-orange-950 mt-1 flex items-center gap-1">
                        {popupData.word}
                    </h4>
                </div>
                <button
                    onClick={() => setPopupData(null)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                    title="Close"
                >
                    ✕
                </button>
            </div>

            <div className="text-sm text-gray-700 leading-relaxed font-medium bg-orange-50/50 p-2.5 rounded-lg border border-orange-100/60">
                {popupData.meaning}
            </div>

            <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                <span className="text-gray-500">
                    Selected: <span className="font-semibold text-gray-700">{popupData.query}</span>
                </span>
                <a
                    href={`/pracheena-qa?search=${encodeURIComponent(popupData.word)}`}
                    className="text-orange-600 font-semibold hover:underline flex items-center gap-1"
                >
                    Search in QA →
                </a>
            </div>
        </div>
    );
};

export default VaideekaDictionaryPopup;
